import { Cancel } from '@mui/icons-material';
import {
  Button,
  Divider,
  Drawer,
  FormControl,
  FormLabel,
  Grid2,
  TextField,
  Typography
} from '@mui/material';
import { Tax } from '@thro-types/billing';

import { Field, Form, Formik } from 'formik';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { SaveButton } from 'app/components';
import { translations } from 'locales/i18n';

import { taxSettingsValidationSchema, useTaxSettingsInitialValue } from './validation';

interface DrawerProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  tax: Tax | undefined;
  handleSaveClick: (values: any) => Promise<void>;
}

export const TaxDrawer = (props: DrawerProps) => {
  const { t } = useTranslation();
  return (
    <Drawer
      anchor="right"
      open={props.open}
      PaperProps={{
        sx: { width: { xs: '70%', md: '35%', xl: '25%' } }
      }}
      onClose={() => props.setOpen(false)}
    >
      <Formik
        initialValues={useTaxSettingsInitialValue(props.tax)}
        onSubmit={values => props.handleSaveClick(values)}
        validationSchema={taxSettingsValidationSchema(t)}
        validateOnMount
      >
        {({ touched, errors, isValid }) => (
          <Form>
            <Grid2
              container
              padding={2}
              flexDirection={'column'}
              height={'100vh'}
              justifyContent={'flex-end'}
              gap={2}
              width={'100%'}
            >
              <Grid2 container size={12} gap={1} flexDirection={'column'}>
                <Typography variant="h5" fontWeight={'bold'}>
                  {props.tax?.key
                    ? t(translations.pages.billingSettings.tax.drawer.update())
                    : t(translations.pages.billingSettings.tax.drawer.create())}
                </Typography>
                <Divider />
              </Grid2>
              <Grid2 container direction={'column'} gap={1}>
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.pages.billingSettings.tax.key())}</FormLabel>
                  <Field
                    as={TextField}
                    id="key"
                    name="key"
                    disabled={props.tax?.key}
                    error={touched.key && Boolean(errors.key)}
                    helperText={touched.key && errors.key}
                    size="small"
                  />
                </FormControl>
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.pages.billingSettings.tax.text())}</FormLabel>
                  <Field
                    as={TextField}
                    id="text"
                    name="text"
                    error={touched.text && Boolean(errors.text)}
                    helperText={touched.text && errors.text}
                    size="small"
                  />
                </FormControl>
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.pages.billingSettings.tax.taxRate())}</FormLabel>
                  <Field
                    as={TextField}
                    id="taxRate"
                    name="taxRate"
                    type="number"
                    error={touched.taxRate && Boolean(errors.taxRate)}
                    helperText={touched.taxRate && errors.taxRate}
                    size="small"
                  />
                </FormControl>
              </Grid2>
              <Grid2 size={12} alignContent={'center'} marginTop={'auto'}>
                <Divider sx={{ marginLeft: 1, marginRight: 0.6, flex: 1 }} />
              </Grid2>
              <Grid2 container justifyContent={'flex-end'} gap={2} alignItems={'flex-end'}>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => props.setOpen(false)}
                >
                  {t(translations.common.cancel())}
                </Button>
                <SaveButton isSubmitting={false} isValid={isValid} />
              </Grid2>
            </Grid2>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};
